import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  CheckCircle, 
  XCircle, 
  User,
  Calendar,
  MapPin,
  Clock,
  TrendingUp,
  Activity,
  Award
} from "lucide-react";
import { getPositionLabel } from "@/utils/positions";

const ApplicationManagement = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [showPlayerDetail, setShowPlayerDetail] = useState(false);

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const response = await axios.get(`${API}/captain/pending-applications`);
      setApplications(response.data);
    } catch (error) {
      console.error("Error fetching applications:", error);
      toast.error("Başvurular yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (applicationId) => {
    setProcessing(applicationId);
    try {
      await axios.post(`${API}/applications/${applicationId}/accept`);
      toast.success("Başvuru kabul edildi!");
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Başvuru kabul edilemedi");
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (applicationId) => {
    if (!confirm("Başvuruyu reddetmek istediğinize emin misiniz?")) {
      return;
    }

    setProcessing(applicationId);
    try {
      await axios.post(`${API}/applications/${applicationId}/reject`);
      toast.success("Başvuru reddedildi");
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Başvuru reddedilemedi");
    } finally {
      setProcessing(null);
    }
  };

  const handleViewPlayer = async (userId) => {
    try {
      const response = await axios.get(`${API}/users/${userId}`);
      setSelectedPlayer(response.data);
      setShowPlayerDetail(true);
    } catch (error) {
      toast.error("Oyuncu bilgileri yüklenemedi");
    }
  };

  const getPositionBadgeColor = (position) => {
    const colors = {
      goalkeeper: "bg-yellow-100 text-yellow-800",
      defender: "bg-blue-100 text-blue-800",
      midfielder: "bg-green-100 text-green-800",
      forward: "bg-red-100 text-red-800"
    };
    return colors[position] || "bg-gray-100 text-gray-800";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-[80vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Başvurular yükleniyor...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Navbar user={user} onLogout={onLogout} />
      
      <main className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button 
            variant="outline" 
            onClick={() => navigate("/captain-dashboard")}
            className="mb-4"
          >
            ← Kaptan Paneli'ne Dön
          </Button>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent mb-2">
            Başvuru Yönetimi
          </h1>
          <p className="text-gray-600">
            Bekleyen başvuruları değerlendir ve takımını oluştur
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="border-l-4 border-l-orange-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Bekleyen Başvurular</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-orange-600">{applications.length}</p>
            </CardContent>
          </Card>
        </div>

        {/* Applications List */}
        {applications.length === 0 ? (
          <Card>
            <CardContent className="py-16">
              <div className="text-center text-gray-500">
                <User className="h-16 w-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg">Bekleyen başvuru yok</p>
                <p className="text-sm mt-2">Yeni başvurular geldiğinde burada görünecek</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {applications.map((app) => (
              <Card key={app.id} className="hover:shadow-md transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex flex-col md:flex-row gap-6">
                    {/* Player Info */}
                    <div className="flex items-start gap-4 flex-1">
                      <Avatar className="h-16 w-16">
                        <AvatarImage src={app.userProfileImage} />
                        <AvatarFallback className="text-xl">{app.userName?.[0]}</AvatarFallback>
                      </Avatar>
                      
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h3 className="text-xl font-bold">{app.userName}</h3>
                            <Badge className={`mt-1 ${getPositionBadgeColor(app.position)}`}>
                              {getPositionLabel(app.detailedPosition)}
                            </Badge>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewPlayer(app.userId)}
                          >
                            Profili Gör
                          </Button>
                        </div>

                        {/* Stats */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                          <div className="flex items-center gap-2">
                            <Award className="h-4 w-4 text-emerald-600" />
                            <div>
                              <p className="text-xs text-gray-600">Puan</p>
                              <p className="font-semibold">{app.userPoints || 0}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Activity className="h-4 w-4 text-blue-600" />
                            <div>
                              <p className="text-xs text-gray-600">Kondisyon</p>
                              <p className="font-semibold">{app.userKondisyon || 50}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <TrendingUp className="h-4 w-4 text-purple-600" />
                            <div>
                              <p className="text-xs text-gray-600">Teknik</p>
                              <p className="font-semibold">
                                {Math.round((
                                  (app.technicalCard?.passing || 0) +
                                  (app.technicalCard?.shooting || 0) +
                                  (app.technicalCard?.ballControl || 0) +
                                  (app.technicalCard?.dribbling || 0)
                                ) / 4)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-gray-600" />
                            <div>
                              <p className="text-xs text-gray-600">Başvuru</p>
                              <p className="font-semibold text-xs">
                                {new Date(app.createdAt).toLocaleDateString('tr-TR')}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Match Info */}
                        {app.matchDetails && (
                          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                            <p className="text-sm font-semibold mb-2">Maç Bilgileri</p>
                            <div className="flex flex-wrap gap-3 text-sm text-gray-600">
                              <span className="flex items-center gap-1">
                                <MapPin className="h-4 w-4" />
                                {app.matchDetails.venueName}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="h-4 w-4" />
                                {app.matchDetails.date}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="h-4 w-4" />
                                {app.matchDetails.time}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex md:flex-col gap-3 md:w-40">
                      <Button
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => handleAccept(app.id)}
                        disabled={processing === app.id}
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Kabul Et
                      </Button>
                      <Button
                        variant="destructive"
                        className="flex-1"
                        onClick={() => handleReject(app.id)}
                        disabled={processing === app.id}
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Reddet
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Player Detail Dialog */}
      <Dialog open={showPlayerDetail} onOpenChange={setShowPlayerDetail}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Oyuncu Detayları</DialogTitle>
          </DialogHeader>
          {selectedPlayer && (
            <div className="space-y-6">
              {/* Player Header */}
              <div className="flex items-center gap-4">
                <Avatar className="h-24 w-24">
                  <AvatarImage src={selectedPlayer.profileImage} />
                  <AvatarFallback className="text-3xl">{selectedPlayer.name?.[0]}</AvatarFallback>
                </Avatar>
                <div>
                  <h2 className="text-2xl font-bold">{selectedPlayer.name}</h2>
                  <p className="text-gray-600">{selectedPlayer.email}</p>
                  {selectedPlayer.position && (
                    <Badge className={`mt-2 ${getPositionBadgeColor(selectedPlayer.position)}`}>
                      {selectedPlayer.position === 'goalkeeper' ? 'Kaleci' :
                       selectedPlayer.position === 'defender' ? 'Defans' :
                       selectedPlayer.position === 'midfielder' ? 'Orta Saha' : 'Forvet'}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Stats Overview */}
              <Card>
                <CardHeader>
                  <CardTitle>Genel İstatistikler</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-emerald-50 rounded-lg">
                      <p className="text-2xl font-bold text-emerald-600">
                        {selectedPlayer.record?.totalPoints || 0}
                      </p>
                      <p className="text-sm text-gray-600">Toplam Puan</p>
                    </div>
                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                      <p className="text-2xl font-bold text-blue-600">
                        {selectedPlayer.record?.matchesPlayed || 0}
                      </p>
                      <p className="text-sm text-gray-600">Maç</p>
                    </div>
                    <div className="text-center p-3 bg-purple-50 rounded-lg">
                      <p className="text-2xl font-bold text-purple-600">
                        {selectedPlayer.kondisyon || 50}
                      </p>
                      <p className="text-sm text-gray-600">Kondisyon</p>
                    </div>
                    <div className="text-center p-3 bg-orange-50 rounded-lg">
                      <p className="text-2xl font-bold text-orange-600">
                        {selectedPlayer.record?.mvp || 0}
                      </p>
                      <p className="text-sm text-gray-600">MVP</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Match Record */}
              <Card>
                <CardHeader>
                  <CardTitle>Maç Performansı</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                    <div>
                      <p className="text-3xl font-bold text-emerald-600">
                        {selectedPlayer.record?.goals || 0}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">Gol</p>
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-blue-600">
                        {selectedPlayer.record?.assists || 0}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">Asist</p>
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-purple-600">
                        {selectedPlayer.record?.saves || 0}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">Kurtarış</p>
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-orange-600">
                        {selectedPlayer.record?.mvp || 0}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">MVP</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApplicationManagement;
